import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormLabel,
} from '@mui/material';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import { ScrapingTask } from '../hooks/useScrapingTasks';
import { TaskGroup, useTaskGroups } from '../hooks/useTaskGroups';
import { loadStoreBrandNames, loadStoreProductsForBrand } from '../utils/storeCatalog';

interface Props {
  open: boolean;
  onClose: () => void;
  tasks: ScrapingTask[];
  group?: TaskGroup | null;
  onSaved?: () => void;
  onAssemble?: (group?: TaskGroup | null) => void;
}

function taskLabel(task: ScrapingTask): string {
  const u = task.urls && task.urls[0];
  if (!u) return task.marketplace_definition_id;
  try {
    return new URL(u).hostname;
  } catch {
    return task.marketplace_definition_id;
  }
}

export const TaskGroupDialog: React.FC<Props> = ({
  open,
  onClose,
  tasks,
  group,
  onSaved,
  onAssemble,
}) => {
  const { createGroup, updateGroup } = useTaskGroups();
  const editing = Boolean(group?.id);
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [brands, setBrands] = useState<string[]>([]);
  const [product, setProduct] = useState('');
  const [availableProducts, setAvailableProducts] = useState<string[]>([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(group?.name || '');
    setBrand(group?.brand_name || '');
    setProduct(group?.product_name || '');
    setSelectedTaskIds(new Set(group?.task_ids || []));
    const loadBrands = async () => {
      try {
        setBrands(await loadStoreBrandNames());
      } catch {
        setBrands([]);
      }
    };
    loadBrands();
  }, [open, group]);

  useEffect(() => {
    if (!brand) {
      setAvailableProducts([]);
      return;
    }
    const load = async () => {
      try {
        const products = await loadStoreProductsForBrand(brand);
        setAvailableProducts(products.map(p => p.name));
      } catch {
        setAvailableProducts([]);
      }
    };
    load();
  }, [brand]);

  const filteredTasks = useMemo(() => {
    if (!brand) return [] as ScrapingTask[];
    return tasks.filter(t => {
      const b = (t.brand_name || '').toLowerCase();
      if (b !== brand.toLowerCase()) return false;
      if (!product) return true;
      return (t.product_name || '').toLowerCase() === product.toLowerCase();
    });
  }, [tasks, brand, product]);

  const toggleTask = (taskId: string) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim() || selectedTaskIds.size === 0) return;
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        brand_name: brand || null,
        product_name: product || null,
        task_ids: Array.from(selectedTaskIds),
      };
      if (editing && group) {
        await updateGroup(group.id, payload);
      } else {
        await createGroup(payload);
      }
      if (onSaved) onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{editing ? 'Edit Task Group' : 'Create Task Group'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
          <FormLabel sx={{ px: 0.5, color: 'text.secondary' }}>Group name</FormLabel>
          <TextField
            fullWidth
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="China platforms"
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Brand</InputLabel>
            <Select value={brand} label="Brand" onChange={e => setBrand(String(e.target.value))}>
              <MenuItem value="">Select brand</MenuItem>
              {brands.map(b => (
                <MenuItem key={b} value={b}>
                  {b}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth disabled={!brand}>
            <InputLabel>Product</InputLabel>
            <Select
              value={product}
              label="Product"
              onChange={e => setProduct(String(e.target.value))}
            >
              <MenuItem value="">All products</MenuItem>
              {availableProducts.map(p => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {!brand ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select a brand to choose tasks. Product is optional.
          </Typography>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Tasks ({filteredTasks.length})
            </Typography>
            <List dense>
              {filteredTasks.map(t => {
                const pages = t.max_pages ?? 1;
                const stage2 = t.max_stage2_products ?? 5;
                const secondary = `${t.product_name || '—'} · pages: ${pages}, stage2: ${stage2}`;
                const checked = selectedTaskIds.has(t.id);
                return (
                  <ListItem key={t.id} button onClick={() => toggleTask(t.id)}>
                    <ListItemIcon>
                      <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                    </ListItemIcon>
                    <ListItemText primary={taskLabel(t)} secondary={secondary} />
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Box>
          {onAssemble && (
            <Button
              startIcon={<AutoAwesome />}
              onClick={() => {
                onAssemble(group || null);
              }}
            >
              Assemble with AI
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!name.trim() || selectedTaskIds.size === 0 || saving}
          >
            {editing ? 'Save Group' : 'Create Group'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TaskGroupDialog;
